import Dexie, { Table } from 'dexie';
import { ICSElement } from '../types/interfaces';
// import { StorageAdapter } from '../types/persistence-interfaces';

/**
 * Структура элемента в IndexedDB
 */
interface StoredElement {
  id: string;
  name?: string;
  index?: number;
  data: Record<string, any>;
  serializedData: string;
  createdAt: number;
  updatedAt: number;
  version: number;
}

/**
 * База данных для хранения элементов
 */
class CSElementDatabase extends Dexie {
  elements!: Table<StoredElement>;
  metadata!: Table<{ key: string; value: any }>;

  constructor() {
    super('CSElementDatabase');
    
    this.version(1).stores({
      elements: 'id, name, index, createdAt, updatedAt, version',
      metadata: 'key'
    });
  }
}

/**
 * Адаптер для IndexedDB с использованием Dexie
 */
export class IndexedDBAdapter {
  private db: CSElementDatabase;
  private isInitialized = false;

  constructor(private dbName: string = 'CSElementDatabase') {
    this.db = new CSElementDatabase();
  }

  async initialize(): Promise<void> {
    if (this.isInitialized) return;

    try {
      await this.db.open();
      this.isInitialized = true;
    } catch (error) {
      throw new Error(`Не удалось инициализировать IndexedDB: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  async save(elements: ICSElement[]): Promise<void> {
    await this.initialize();

    const transaction = this.db.transaction('rw', this.db.elements, async () => {
      for (const element of elements) {
        const storedElement: StoredElement = {
          id: element.id,
          name: element.name,
          index: element.index,
          data: this.extractElementData(element),
          serializedData: this.serializeElement(element),
          createdAt: Date.now(),
          updatedAt: Date.now(),
          version: 1
        };

        await this.db.elements.put(storedElement);
      }
    });

    await transaction;
  }

  async load(): Promise<ICSElement[]> {
    await this.initialize();

    const storedElements = await this.db.elements.toArray();
    const elements: ICSElement[] = [];

    for (const stored of storedElements) {
      try {
        const element = this.deserializeElement(stored.serializedData);
        elements.push(element);
      } catch (error) {
        console.warn(`Не удалось десериализовать элемент ${stored.id}:`, error);
      }
    }

    return elements;
  }

  async loadById(id: string): Promise<ICSElement | null> {
    await this.initialize();

    const stored = await this.db.elements.get(id);
    if (!stored) return null;

    try {
      return this.deserializeElement(stored.serializedData);
    } catch (error) {
      console.warn(`Не удалось десериализовать элемент ${id}:`, error);
      return null;
    }
  }

  async delete(id: string): Promise<boolean> {
    await this.initialize();

    const deleted = await this.db.elements.delete(id);
    return deleted !== undefined;
  }

  async clear(): Promise<void> {
    await this.initialize();

    await this.db.elements.clear();
  }

  async count(): Promise<number> {
    await this.initialize();

    return await this.db.elements.count();
  }

  async exists(id: string): Promise<boolean> {
    await this.initialize();

    const count = await this.db.elements.where('id').equals(id).count();
    return count > 0;
  }

  async query(filter: {
    name?: string;
    index?: number;
    limit?: number;
    offset?: number;
  }): Promise<ICSElement[]> {
    await this.initialize();

    let query = this.db.elements.toCollection();

    if (filter.name) {
      query = query.filter(item => item.name === filter.name);
    }

    if (filter.index !== undefined) {
      query = query.filter(item => item.index === filter.index);
    }

    if (filter.offset) {
      query = query.offset(filter.offset);
    }

    if (filter.limit) {
      query = query.limit(filter.limit);
    }

    const storedElements = await query.toArray();
    const elements: ICSElement[] = [];

    for (const stored of storedElements) {
      try {
        const element = this.deserializeElement(stored.serializedData);
        elements.push(element);
      } catch (error) {
        console.warn(`Не удалось десериализовать элемент ${stored.id}:`, error);
      }
    }

    return elements;
  }

  async updateMetadata(key: string, value: any): Promise<void> {
    await this.initialize();

    await this.db.metadata.put({ key, value });
  }

  async getMetadata(key: string): Promise<any> {
    await this.initialize();

    const record = await this.db.metadata.get(key);
    return record?.value;
  }

  async close(): Promise<void> {
    if (this.isInitialized) {
      this.db.close();
      this.isInitialized = false;
    }
  }

  private extractElementData(element: ICSElement): Record<string, any> {
    const data: Record<string, any> = {};
    
    // Извлекаем основные данные элемента
    if (element.getData) {
      try {
        // Пытаемся получить все данные элемента
        const allData = element.getData('');
        if (typeof allData === 'object' && allData !== null) {
          Object.assign(data, allData);
        }
      } catch {
        // Если не удается получить все данные, игнорируем
      }
    }

    return data;
  }

  private serializeElement(element: ICSElement): string {
    try {
      // Используем встроенную сериализацию элемента, если доступна
      if (typeof (element as any).serialize === 'function') {
        const serialized = (element as any).serialize({
          includeChildren: true,
          includeData: true,
          includeMetadata: true
        });
        return JSON.stringify(serialized);
      }

      // Fallback сериализация
      return JSON.stringify({
        id: element.id,
        name: element.name,
        index: element.index,
        data: this.extractElementData(element)
      });
    } catch (error) {
      throw new Error(`Ошибка сериализации элемента ${element.id}: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  private deserializeElement(serializedData: string): ICSElement {
    try {
      const data = JSON.parse(serializedData);
      
      // Пытаемся использовать встроенную десериализацию, если доступна
      if (typeof (global as any).CSElement?.deserialize === 'function') {
        return (global as any).CSElement.deserialize(data);
      }

      // Fallback десериализация - создаем базовый объект
      return {
        id: data.id,
        name: data.name,
        index: data.index,
        getData: (key: string) => data.data?.[key],
        setData: async (key: string, value: any) => {
          if (!data.data) data.data = {};
          data.data[key] = value;
        },
        deleteData: async (key: string) => {
          if (data.data) {
            delete data.data[key];
            return true;
          }
          return false;
        },
        // Заглушки для других методов
        mainOwner: null,
        data: new Map(Object.entries(data.data || {})),
        getOwner: () => null,
        getOwnerAsync: async () => null,
        getElement: () => null,
        getElementAsync: async () => null,
        getRelativeIndex: () => -1,
        elementsCount: () => 0,
        ownersCount: () => 0,
        getAllElements: () => [],
        getAllOwners: () => [],
        lastElement: null,
        addElement: async () => { throw new Error('Not implemented'); },
        removeElement: async () => false,
        enterLock: async () => {},
        leaveLock: async () => {},
        isLocked: () => false
      } as ICSElement;
    } catch (error) {
      throw new Error(`Ошибка десериализации элемента: ${error instanceof Error ? error.message : String(error)}`);
    }
  }

  /**
   * Получение информации о базе данных
   */
  async getInfo(): Promise<{
    name: string;
    version: number;
    size: number;
    elementCount: number;
  }> {
    await this.initialize();

    const elementCount = await this.count();
    
    return {
      name: this.dbName,
      version: 1,
      size: 0, // IndexedDB не предоставляет точную информацию о размере
      elementCount
    };
  }

  /**
   * Компактификация базы данных (очистка неиспользуемых данных)
   */
  async compact(): Promise<void> {
    await this.initialize();

    // IndexedDB автоматически управляет пространством,
    // но мы можем очистить старые версии или поврежденные записи
    const allElements = await this.db.elements.toArray();
    const validElements = allElements.filter(element => {
      try {
        this.deserializeElement(element.serializedData);
        return true;
      } catch {
        return false;
      }
    });

    if (validElements.length < allElements.length) {
      await this.db.elements.clear();
      await this.db.elements.bulkAdd(validElements);
    }
  }
} 